import React, { useMemo } from 'react';

const STATUS_CONFIG = {
  REFUND_PENDING: {
    icon: '🟡',
    label: 'Pending',
    color: '#f59e0b',
    bg: 'rgba(245,158,11,0.12)',
    description: 'Refund in progress — retrying every few minutes',
  },
  REFUNDED: {
    icon: '✅',
    label: 'Refunded',
    color: '#10b981',
    bg: 'rgba(16,185,129,0.12)',
    description: 'Money returned to customer',
  },
  EXPIRED: {
    icon: '⚫',
    label: 'Expired (No Payment)',
    color: '#6b7280',
    bg: 'rgba(107,114,128,0.12)',
    description: 'Customer never paid — no refund needed',
  },
};

const REASON_LABELS = {
  timer_expired:     '⏱ QR timed out',
  glass_not_placed:  '🥤 No glass placed',
  orphaned_payment:  '🔌 Kiosk restarted',
  juice_switched:    '🔄 Juice switched',
};

// Kiosk writes UTC timestamps WITHOUT 'Z' (e.g. "2026-05-22T12:45:00").
// JavaScript's Date() treats bare ISO strings as LOCAL time (IST), making
// them appear 5.5h in the past. Fix: always append Z to force UTC parsing.
function parseTs(ts) {
  if (!ts) return null;
  if (typeof ts === 'number') return new Date(ts * 1000);
  // If string has no timezone marker, treat as UTC
  const s = String(ts);
  if (!s.endsWith('Z') && !s.includes('+') && !s.includes('-', 10)) {
    return new Date(s + 'Z');
  }
  return new Date(s);
}

function timeAgo(isoTimestamp) {
  if (!isoTimestamp) return '—';
  const date = parseTs(isoTimestamp);
  if (!date) return '—';
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)   return 'Just now';
  if (mins < 60)  return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)   return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function RefundQueuePanel({ refundQueue }) {
  const entries = useMemo(() => {
    if (!refundQueue) return [];
    return Object.entries(refundQueue)
      .map(([id, data]) => ({ id, ...data }))
      .sort((a, b) => (parseTs(b.timestamp) || 0) - (parseTs(a.timestamp) || 0));
  }, [refundQueue]);

  const pending  = entries.filter(e => e.status === 'REFUND_PENDING');
  const refunded = entries.filter(e => e.status === 'REFUNDED');
  const expired  = entries.filter(e => e.status === 'EXPIRED');

  if (entries.length === 0) {
    return (
      <div className="section">
        <div className="section-header">
          <h2 className="section-title">
            <span className="section-title-icon">💸</span>
            Refund Queue
          </h2>
        </div>
        <div className="empty-state">
          <span className="empty-state-icon">✅</span>
          <p>No refund activity yet. This panel tracks every failed dispense and refund.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="section">
      <div className="section-header">
        <h2 className="section-title">
          <span className="section-title-icon">💸</span>
          Refund Queue
        </h2>
        {/* Summary badges */}
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
          {pending.length > 0 && (
            <span style={{
              background: 'rgba(245,158,11,0.15)', color: '#f59e0b',
              borderRadius: '999px', padding: '3px 12px', fontSize: '0.8rem', fontWeight: 600,
            }}>
              🟡 {pending.length} Pending
            </span>
          )}
          {refunded.length > 0 && (
            <span style={{
              background: 'rgba(16,185,129,0.15)', color: '#10b981',
              borderRadius: '999px', padding: '3px 12px', fontSize: '0.8rem', fontWeight: 600,
            }}>
              ✅ {refunded.length} Refunded
            </span>
          )}
          {expired.length > 0 && (
            <span style={{
              background: 'rgba(107,114,128,0.15)', color: '#6b7280',
              borderRadius: '999px', padding: '3px 12px', fontSize: '0.8rem', fontWeight: 600,
            }}>
              ⚫ {expired.length} Expired
            </span>
          )}
        </div>
      </div>

      {/* How refunds work info box */}
      {pending.length > 0 && (
        <div style={{
          background: 'rgba(245,158,11,0.08)',
          border: '1px solid rgba(245,158,11,0.25)',
          borderRadius: '10px', padding: '0.75rem 1rem',
          fontSize: '0.82rem', color: '#d97706', marginBottom: '1rem',
        }}>
          <strong>🕐 Refund Timeline:</strong> The kiosk retries automatically at 5s → 10s → 20s → 30s → 60s after failure.
          If all retries fail, it checks again every 5 minutes. Customers typically receive money within <strong>2–5 minutes</strong>.
        </div>
      )}

      {/* Entry list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
        {entries.map(entry => {
          const cfg = STATUS_CONFIG[entry.status] || STATUS_CONFIG.REFUND_PENDING;
          const reason = REASON_LABELS[entry.reason] || entry.reason || '—';
          return (
            <div
              key={entry.id}
              style={{
                background: cfg.bg,
                border: `1px solid ${cfg.color}44`,
                borderLeft: `4px solid ${cfg.color}`,
                borderRadius: '10px',
                padding: '0.75rem 1rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '0.5rem',
              }}
            >
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary)' }}>
                  {cfg.icon} {entry.order_id || entry.id}
                </div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                  {reason} &nbsp;·&nbsp; {timeAgo(entry.timestamp)}
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  {cfg.description}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontWeight: 700, fontSize: '1.1rem', color: cfg.color }}>
                  ₹{Number(entry.amount || 0).toFixed(2)}
                </div>
                <div style={{
                  fontSize: '0.75rem', color: cfg.color,
                  background: `${cfg.color}22`, borderRadius: '999px',
                  padding: '2px 10px', marginTop: '4px', display: 'inline-block',
                }}>
                  {cfg.label}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
